import React from 'react';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { CardList } from '@/components/ui/CardList';

export function ProjectCostsList({ costItems }: { costItems: any[] }) {
  return (
    <div className="bg-white rounded-md border border-slate-100  overflow-hidden">
      <div className="p-4 border-b border-slate-50">
        <h3 className="text-sm font-normal text-slate-900 tracking-wider">Danh sách chi phí</h3>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50 text-xs font-normal text-slate-400 border-b border-slate-100">
              <th className="py-3 px-4 w-10 font-normal">#</th>
              <th className="py-3 px-4 font-normal">Hạng mục chi phí</th>
              <th className="py-3 px-4 font-normal">Loại chi phí</th>
              <th className="py-3 px-4 text-right font-normal">Ngân sách (VND)</th>
              <th className="py-3 px-4 text-right font-normal">Đã phát sinh (VND)</th>
              <th className="py-3 px-4 text-right font-normal">Đã thanh toán (VND)</th>
              <th className="py-3 px-4 text-right font-normal">Còn lại (VND)</th>
              <th className="py-3 px-4 text-center font-normal">%</th>
              <th className="py-3 px-4 text-center font-normal">Trạng thái</th>
              <th className="py-3 px-4 w-10 font-normal"></th>
            </tr>
          </thead>
          <tbody className="text-xs lg:text-sm divide-y divide-slate-50 font-normal">
            {costItems.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="py-4 px-4 text-slate-400">{item.index}</td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0 border border-slate-50", item.iconBg, item.iconColor)}>
                       <i className={cn(item.icon, "text-base")} />
                    </div>
                    <div className="flex flex-col min-w-0">
                       <span className="font-normal text-slate-700 truncate group-hover:text-emerald-600 transition-colors cursor-pointer">{item.category}</span>
                       <span className="text-xs text-slate-400">{item.subCategory}</span>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4 text-slate-500">{item.type}</td>
                <td className="py-4 px-4 text-right text-slate-700">{formatCurrency(item.budget, '')}</td>
                <td className="py-4 px-4 text-right text-slate-900 font-normal">{formatCurrency(item.actual, '')}</td>
                <td className="py-4 px-4 text-right text-indigo-600 font-normal">{formatCurrency(item.paid, '')}</td>
                <td className="py-4 px-4 text-right text-rose-500 font-normal">{formatCurrency(item.remaining, '')}</td>
                <td className="py-4 px-4 text-center text-xs font-normal text-slate-500">{item.percent}%</td>
                <td className="py-4 px-4 text-center">
                   <span className={cn("px-2 py-0.5 rounded-md text-xs font-normal whitespace-nowrap", item.statusColor)}>{item.status}</span>
                </td>
                <td className="py-4 px-4 text-right">
                   <ActionMenu items={[{ label: 'Chi tiết hạng mục' }, { label: 'Lịch sử thanh toán' }, { label: 'Điều chỉnh ngân sách' }]} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50/30 font-normal text-slate-900 border-t border-slate-100">
             <tr>
                <td colSpan={3} className="py-4 px-4 tracking-wider text-xs">Tổng cộng</td>
                <td className="py-4 px-4 text-right">28.650.000.000</td>
                <td className="py-4 px-4 text-right">16.850.000.000</td>
                <td className="py-4 px-4 text-right text-indigo-600">13.250.000.000</td>
                <td className="py-4 px-4 text-right text-rose-500">11.800.000.000</td>
                <td className="py-4 px-4 text-center text-xs">58.9%</td>
                <td colSpan={2}></td>
             </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile Card List */}
      <div className="lg:hidden p-4">
        <CardList 
          items={costItems}
          renderCard={(item) => (
            <div key={item.id} className="bg-white p-3 rounded-md border border-slate-100 space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-md flex items-center justify-center border border-slate-50", item.iconBg, item.iconColor)}>
                    <i className={cn(item.icon, "text-xl")} />
                  </div>
                  <div className="space-y-0.5">
                    <h4 className="text-xs font-normal text-slate-900">{item.category}</h4>
                    <p className="text-xs font-normal text-slate-400">{item.type}</p>
                  </div>
                </div>
                <ActionMenu items={[{ label: 'Chi tiết' }, { label: 'Thanh toán' }]} />
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-slate-50 pt-3 text-xs">
                 <div className="space-y-1">
                    <p className="text-slate-400 font-normal">Ngân sách</p>
                    <p className="font-normal text-slate-900">{formatCurrency(item.budget, '')}</p>
                 </div>
                 <div className="space-y-1 text-right">
                    <p className="text-slate-400 font-normal">Đã phát sinh</p>
                    <p className="font-normal text-slate-900">{formatCurrency(item.actual, '')}</p>
                 </div>
                 <div className="space-y-1">
                    <p className="text-indigo-500 font-normal">Thanh toán</p>
                    <p className="font-normal text-indigo-600">{formatCurrency(item.paid, '')}</p>
                 </div>
                 <div className="space-y-1 text-right">
                    <p className="text-rose-400 font-normal">Còn lại</p>
                    <p className="font-normal text-rose-500">{formatCurrency(item.remaining, '')}</p>
                 </div>
              </div>
            </div>
          )}
        />
      </div>

      <div className="px-4 border-t border-slate-50 bg-slate-50/30">
        <TablePagination currentPage={1} totalItems={7} pageSize={10} onPageChange={() => {}} itemLabel="hạng mục" />
      </div>
    </div>
  );
}
